import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SectionHeaderSub = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

export const SectionLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    text-decoration: underline;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

export const GateStatGrid = styled(StatGrid)`
  margin-top: 8px;
`;

export const MetricBox = styled(Card)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetricLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const MetricValue = styled.div<{ $color?: string }>`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ $color, theme }) => $color ?? theme.colors.text.primary};
`;

export const MetricNote = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

export const CovenantsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
  gap: 12px;
`;

export const CovenantCard = styled(Card)<{ $status: string }>`
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-left: 4px solid
    ${({ $status, theme }) =>
      $status === 'breach'
        ? '#ef4444'
        : $status === 'warning'
          ? '#f59e0b'
          : $status === 'ok'
            ? '#10b981'
            : theme.colors.border};
`;

export const CovenantHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
`;

export const BankName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const CovenantType = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

export const CovenantTurnoverRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 4px;
`;

export const ProgressBarWrap = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;
  position: relative;
`;

export const ProgressBarFill = styled.div<{ $pct: number; $status: string }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  background: ${({ $status }) =>
    $status === 'breach' ? '#ef4444' : $status === 'warning' ? '#f59e0b' : '#10b981'};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

export const CovenantMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const EmptyCovenantsCard = styled(Card)`
  padding: 16px;
`;

export const GateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

export const GateCard = styled(Card)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const GateHeaderBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const GateBadge = styled.div<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ $color }) => $color};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
`;

export const GateTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const GateSubContract = styled.div`
  font-size: 11px;
  color: #10b981;
  font-weight: 700;
`;

export const GateSubBudget = styled.div`
  font-size: 11px;
  color: #f59e0b;
  font-weight: 700;
`;

export const GateSubAccount = styled.div`
  font-size: 11px;
  color: #0ea5e9;
  font-weight: 700;
`;

export const GateDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`;

/** Таблица прокручивается внутри себя: шапка закреплена в этой области, у длинного списка обе
 *  полосы прокрутки видны в окне. Короткая таблица — по высоте содержимого. */
export const TableWrap = styled(Card)`
  padding: 0;
  max-height: min(70vh, calc(100vh - 260px));
  overflow: auto;
`;

/** Строка прогноза: красная — кассовый разрыв (остаток меньше нуля), жёлтая — ниже резерва. */
export const CashGapTr = styled.tr<{ $level?: string }>`
  background: ${({ $level }) =>
    $level === 'gap'
      ? 'rgba(239, 68, 68, 0.08)'
      : $level === 'below_buffer'
        ? 'rgba(245, 158, 11, 0.08)'
        : 'transparent'};
`;

export const DateTd = styled.td`
  font-weight: 600;
`;

export const InflowsTd = styled.td`
  color: #10b981;
  font-weight: 600;
`;

export const OutflowsTd = styled.td`
  color: #ef4444;
  font-weight: 600;
`;

export const NetFlowTd = styled.td<{ $positive?: boolean }>`
  font-weight: 700;
  color: ${({ $positive }) => ($positive ? '#10b981' : '#ef4444')};
`;

export const ClosingBalanceTd = styled.td`
  font-weight: 700;
`;

export const EmptyTableNoticeTd = styled.td`
  text-align: center;
  padding: 20px;
`;

export const TaxSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
`;

export const LoadingCard = styled(Card)`
  padding: 24px;
`;

export const ErrorCard = styled(Card)`
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const RetryButton = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.4);
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
  font-weight: 700;
  cursor: pointer;
`;
